import { useState } from "react";
import { genres, regions, statuses, sortOptions } from "@/mocks/comics";

export interface ComicFilters {
  genre: string;
  region: string;
  status: string;
  sort: string;
}

interface ComicFilterProps {
  value: ComicFilters;
  onChange: (patch: Partial<ComicFilters>) => void;
  onReset: () => void;
}

interface DropdownProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onSelect: (value: string) => void;
}

function Dropdown({ label, value, options, onSelect }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 whitespace-nowrap cursor-pointer"
      >
        <span className="text-foreground-400">{label}：</span>
        {current ? current.label : "全部"}
        <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? "rotate-180" : ""}`}></i>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)}></div>
          <div className="absolute left-0 top-full z-20 mt-1 w-40 rounded-md border border-background-200 bg-background-50 py-1 shadow-sm animate-fade-in">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onSelect(o.value);
                  setOpen(false);
                }}
                className={`block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-primary-50 hover:text-primary-600 cursor-pointer ${
                  o.value === value ? "text-primary-600 font-medium" : "text-foreground-700"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function ComicFilter({ value, onChange, onReset }: ComicFilterProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const genreOptions = [
    { value: "全部", label: "全部" },
    ...genres.map((g) => ({ value: g, label: g })),
  ];
  const regionOptions = [
    { value: "全部", label: "全部" },
    ...regions.map((r) => ({ value: r, label: r })),
  ];
  const statusOptions = [
    { value: "全部", label: "全部" },
    ...statuses.map((s) => ({ value: s, label: s })),
  ];
  const sortOpts = sortOptions.map((s) => ({ value: s.key, label: s.label }));

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:flex flex-wrap items-center gap-2">
        <Dropdown label="类型" value={value.genre} options={genreOptions} onSelect={(v) => onChange({ genre: v })} />
        <Dropdown label="地区" value={value.region} options={regionOptions} onSelect={(v) => onChange({ region: v })} />
        <Dropdown label="状态" value={value.status} options={statusOptions} onSelect={(v) => onChange({ status: v })} />
        <Dropdown label="排序" value={value.sort} options={sortOpts} onSelect={(v) => onChange({ sort: v })} />
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 rounded-md px-3 py-2 text-sm text-foreground-500 transition-colors hover:text-primary-600 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          重置
        </button>
      </div>

      {/* Mobile trigger */}
      <div className="sticky top-16 z-30 -mx-4 mb-2 flex items-center gap-2 border-b border-background-200 bg-background-100/95 px-4 py-2 backdrop-blur md:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选与排序
        </button>
        <button
          type="button"
          onClick={onReset}
          className="rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-600 whitespace-nowrap cursor-pointer"
        >
          重置
        </button>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setDrawerOpen(false)}></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5 animate-fade-up">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg text-foreground-950">筛选与排序</h3>
              <button type="button" onClick={() => setDrawerOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <p className="mb-2 text-sm font-medium text-foreground-800">类型</p>
                <div className="flex flex-wrap gap-2">
                  {genreOptions.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => onChange({ genre: o.value })}
                      className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap cursor-pointer ${
                        value.genre === o.value ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-foreground-800">地区</p>
                <div className="flex flex-wrap gap-2">
                  {regionOptions.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => onChange({ region: o.value })}
                      className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap cursor-pointer ${
                        value.region === o.value ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-foreground-800">状态</p>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => onChange({ status: o.value })}
                      className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap cursor-pointer ${
                        value.status === o.value ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-foreground-800">排序</p>
                <div className="flex flex-wrap gap-2">
                  {sortOpts.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => onChange({ sort: o.value })}
                      className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap cursor-pointer ${
                        value.sort === o.value ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 py-3 text-sm text-background-50 whitespace-nowrap cursor-pointer"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </>
  );
}